import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { Upload, Search, FileText, Trash2, Send, Loader2, RefreshCw, Copy, FileType, Presentation } from 'lucide-react';
import toast from 'react-hot-toast';
import MessageVoting from '../components/MessageVoting';
import FeedbackModal from '../components/FeedbackModal';

// Document Manager Component
const DocumentManager = React.forwardRef(({ onDocumentUpdate }, ref) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState({});

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/ai-search/documents');
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Expose fetchDocuments to parent component
  useImperativeHandle(ref, () => ({
    fetchDocuments
  }));

  const refreshDocument = async (documentId) => {
    setRefreshing(prev => ({ ...prev, [documentId]: true }));
    try {
      const response = await fetch(`/api/ai-search/refresh/${documentId}`, {
        method: 'POST'
      });
      if (response.ok) {
        await fetchDocuments();
        onDocumentUpdate();
      } else {
        throw new Error('Failed to refresh document');
      }
    } catch (error) {
      console.error('Error refreshing document:', error);
    } finally {
      setRefreshing(prev => ({ ...prev, [documentId]: false }));
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const response = await fetch(`/api/ai-search/documents/${documentId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await fetchDocuments();
        onDocumentUpdate();
      } else {
        throw new Error('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };



  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Manage your uploaded documents and refresh embeddings when content changes.
        </p>
        <button
          onClick={fetchDocuments}
          disabled={loading}
          className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
        >
          <RefreshCw className={`mr-1 ${loading ? 'animate-spin' : ''}`} size={14} />
          Refresh
        </button>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          <FileText className="mx-auto mb-1" size={20} />
          <p className="text-sm">No documents uploaded yet.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileText className="text-gray-400" size={16} />
                <div>
                  <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(doc.file_size)} • {formatDate(doc.uploaded_at)}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      doc.status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : doc.status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {doc.status}
                    </span>
                    {doc.chunk_count && (
                      <span className="text-xs text-gray-500">
                        {doc.chunk_count} chunks
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => refreshDocument(doc.id)}
                  disabled={refreshing[doc.id]}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
                  title="Refresh embeddings"
                >
                  {refreshing[doc.id] ? (
                    <Loader2 className="animate-spin" size={12} />
                  ) : (
                    <RefreshCw size={12} />
                  )}
                </button>
                <button
                  onClick={() => deleteDocument(doc.id)}
                  className="inline-flex items-center px-2 py-1 border border-red-300 rounded text-xs font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  title="Delete document"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const AISearch = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [completedChunks, setCompletedChunks] = useState(0);
  const [awsProcessing, setAwsProcessing] = useState(false);
  const [awsProgress, setAwsProgress] = useState(0);
  const [awsCurrentFile, setAwsCurrentFile] = useState(0);
  const [awsTotalFiles, setAwsTotalFiles] = useState(0);
  const [awsEstimatedTime, setAwsEstimatedTime] = useState(0);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [conversation, setConversation] = useState([]);
  const [userVotes, setUserVotes] = useState({}); // Track user votes for each message
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, messageId: null });
  const [defaultQuestions, setDefaultQuestions] = useState([]); // Default questions after upload
  const [followUpQuestions, setFollowUpQuestions] = useState([]); // Follow-up questions after responses
  const [hasStartedNewConversation, setHasStartedNewConversation] = useState(false); // Track if user started new conversation
  const [archivedConversations, setArchivedConversations] = useState([]); // Archived conversations
  const [showArchived, setShowArchived] = useState(false); // Toggle archived view
  const [gammaLoading, setGammaLoading] = useState({});
  const [gammaProgress, setGammaProgress] = useState({}); // Track loading state for Gamma slides generation
  const [showDocumentManager, setShowDocumentManager] = useState(false); // State for document manager popup
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const documentManagerRef = useRef(null);

  // Helper function to get dynamic icon based on file format
  const getExportIcon = (exportUrl) => {
    if (!exportUrl) return '📊';
    
    // Extract file extension from URL
    const urlParts = exportUrl.split('.');
    const extension = urlParts[urlParts.length - 1]?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'pptx':
        return '📊';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      default:
        return '📊';
    }
  };

  // Load conversation history and stored default questions on component mount
  useEffect(() => {
    fetchConversationHistory();
    fetchStoredDefaultQuestions();
    // Reset the conversation flag on fresh page load
    setHasStartedNewConversation(false);
  }, []);

  // Debug logging for default questions
  useEffect(() => {
    console.log('Default questions state:', defaultQuestions);
    console.log('Conversation length:', conversation.length);
    console.log('Has started new conversation:', hasStartedNewConversation);
    console.log('Should show default questions:', defaultQuestions.length > 0 && !hasStartedNewConversation);
  }, [defaultQuestions, conversation.length, hasStartedNewConversation]);

  const fetchConversationHistory = async () => {
    try {
      const response = await fetch('/api/ai-search/conversations');
      if (response.ok) {
        const data = await response.json();
        const history = data.conversations || [];
        const formattedHistory = [];
        history.forEach(conv => {
          formattedHistory.push({
            type: 'user',
            message: conv.question
          });
          formattedHistory.push({
            type: 'assistant',
            message: conv.answer,
            conversationId: conv.id, // Load conversation ID for proper identification
            gammaUrl: conv.gammaUrl || null, // Load existing Gamma slide URL
            exportUrl: conv.exportUrl || null // Load existing PPTX export URL
          });
        });
        setConversation(formattedHistory);
        
        // Initialize gammaProgress for existing conversations with Gamma URLs
        const initialGammaProgress = {};
        formattedHistory.forEach((msg, index) => {
          if (msg.type === 'assistant' && (msg.gammaUrl || msg.exportUrl)) {
            initialGammaProgress[index] = {
              completed: true,
              gammaUrl: msg.gammaUrl,
              exportUrl: msg.exportUrl
            };
          }
        });
        setGammaProgress(initialGammaProgress);
      }
    } catch (error) {
      console.error('Error fetching conversation history:', error);
    }
  };

  const fetchArchivedConversations = async () => {
    try {
      const response = await fetch('/api/ai-search/conversations/archived');
      if (response.ok) {
        const data = await response.json();
        setArchivedConversations(data.archivedConversations || []);
      }
    } catch (error) {
      console.error('Error fetching archived conversations:', error);
    }
  };

  const fetchStoredDefaultQuestions = async () => {
    try {
      console.log('🔄 Fetching stored default questions...');
      // Get all documents to fetch their default questions
      const response = await fetch('/api/ai-search/documents');
      if (response.ok) {
        const data = await response.json();
        const documents = data.documents || [];
        console.log(`📄 Found ${documents.length} documents`);
        
        if (documents.length > 0) {
          // Try to fetch questions for the most recent document first
          const latestDoc = documents[documents.length - 1];
          console.log(`🔍 Checking latest document: ${latestDoc.id} - ${latestDoc.filename}`);
          
          const questionsResponse = await fetch(`/api/ai-search/default-questions/${latestDoc.id}`);
          if (questionsResponse.ok) {
            const questionsData = await questionsResponse.json();
            console.log(`📊 Questions response:`, questionsData);
            
            if (questionsData.success && questionsData.questions.length > 0) {
              console.log(`✅ Setting default questions:`, questionsData.questions);
              setDefaultQuestions(questionsData.questions);
              return; // Found questions, no need to check other documents
            }
          }
          
          // If no questions found for the latest document, try other documents
          for (let i = documents.length - 2; i >= 0; i--) {
            const doc = documents[i];
            console.log(`🔍 Checking document: ${doc.id} - ${doc.filename}`);
            
            const questionsResponse = await fetch(`/api/ai-search/default-questions/${doc.id}`);
            if (questionsResponse.ok) {
              const questionsData = await questionsResponse.json();
              if (questionsData.success && questionsData.questions.length > 0) {
                console.log(`✅ Setting default questions from document ${doc.id}:`, questionsData.questions);
                setDefaultQuestions(questionsData.questions);
                return; // Found questions
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching stored default questions:', error);
    }
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    // Validate file count (100 files limit)
    const maxFiles = 100;
    if (selectedFiles.length > maxFiles) {
      toast.error(`Too many files selected. Maximum ${maxFiles} files allowed per upload.`);
      return;
    }
    
    // Validate file sizes (500MB limit)
    const maxSizeMB = 500;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    const validFiles = selectedFiles.filter(file => {
      if (file.size > maxSizeBytes) {
        toast.error(`${file.name} is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is ${maxSizeMB}MB.`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadAndProcess = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setTotalChunks(0);
    setCompletedChunks(0);
    
    // Generate upload ID for progress tracking
    const uploadId = Date.now().toString();
    
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('uploadId', uploadId);

    try {
      // First, get initial chunk estimation for display
      const initialEstimate = files.reduce((total, file) => {
        const fileSizeMB = file.size / (1024 * 1024);
        // Estimate: 20MB chunks (much larger now)
        const estimatedChunks = Math.ceil(fileSizeMB / 20);
        return total + estimatedChunks;
      }, 0);
      
      setTotalChunks(initialEstimate);

      // Declare progressInterval at function scope
      let progressInterval = null;

      // Start progress tracking with polling
      progressInterval = setInterval(async () => {
        try {
          // Poll for progress updates during processing
          const progressResponse = await fetch(`/api/ai-search/progress/${uploadId}`);
          if (progressResponse.ok) {
            const progress = await progressResponse.json();
            setCompletedChunks(progress.completedChunks || 0);
            setTotalChunks(progress.totalChunks || 0);
            
            // Check for AWS processing status
            if (progress.awsProcessing) {
              setAwsProcessing(true);
              setAwsCurrentFile(progress.awsCurrent || 0);
              setAwsTotalFiles(progress.awsTotal || 0);
              setAwsEstimatedTime(progress.awsEstimatedTime || 0);
              
              // Calculate PDF OCR progress percentage based on status
              if (progress.awsTotal > 0) {
                if (progress.awsStatus === 'completed') {
                  // Job completed, show 100%
                  setAwsProgress(100);
                } else {
                  // Job in progress, show accurate progress (50% for 1/2, 33% for 1/3, etc.)
                  const awsPercentage = Math.round((progress.awsCurrent / progress.awsTotal) * 100);
                  setAwsProgress(awsPercentage); // Show actual progress percentage
                }
              }
            }
            
            if (progress.totalChunks > 0) {
              const percentage = Math.round((progress.completedChunks / progress.totalChunks) * 100);
              setUploadProgress(Math.min(percentage, 95));
            }
            
            // Stop polling if completed
            if (progress.status === 'completed') {
              clearInterval(progressInterval);
              setUploadProgress(100);
              setAwsProcessing(false);
            }
          } else if (progressResponse.status === 404) {
            // Progress not found yet, continue polling
            console.log(`Progress not found for ${uploadId}, continuing to poll...`);
          }
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }, 1000); // Poll every 1 second
      
      // Stop polling after 5 minutes (300 seconds)
      setTimeout(() => {
        if (progressInterval) {
          clearInterval(progressInterval);
        }
      }, 300000);

      const response = await fetch('/api/ai-search/upload', {
        method: 'POST',
        body: formData,
      });

      // Clear the initial progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      if (response.ok) {
        const result = await response.json();
        
        // Set final progress to 100% when upload is complete
        setUploadProgress(100);
        
        setFiles([]);
        const successCount = result.results.filter(r => r.status === 'success').length;
        
        // Get actual chunk count from the result
        const actualChunks = result.results.reduce((total, r) => {
          return total + (r.chunkCount || 0);
        }, 0);
        
        setConversation(prev => [...prev, {
          type: 'system',
          message: `Successfully processed ${successCount} files with ${actualChunks} chunks.\n\nRefreshing document list...`
        }]);
        
        // Auto-refresh document list FIRST (before LLM processing)
        if (documentManagerRef.current && documentManagerRef.current.fetchDocuments) {
          documentManagerRef.current.fetchDocuments();
        }
        
        // Update status message
        setConversation(prev => [...prev, {
          type: 'system',
          message: `Document list updated. Generating automatic summary...`
        }]);
        
        // Auto-generate summary for uploaded documents
        await generateAutoSummary(result.results);
        
        // Generate default questions based on uploaded documents
        await generateDefaultQuestions(result.results);
      } else {
        const errorData = await response.json();
        if (response.status === 413) {
          // Handle token limit exceeded
          setConversation(prev => [...prev, {
            type: 'error',
            message: `Upload blocked: ${errorData.error}\n\n📊 **Token Analysis:**\n• Current documents: ${errorData.currentTokens?.toLocaleString()} tokens\n• File "${errorData.filename}": ${errorData.newFileTokens?.toLocaleString()} tokens\n• Total would be: ${errorData.totalTokens?.toLocaleString()} tokens\n• **Claude limit: ${errorData.limit?.toLocaleString()} tokens**\n\n${errorData.message}`
          }]);
        } else if (response.status === 400 && errorData.code === 'LIMIT_FILE_COUNT') {
          // Handle file count limit exceeded
          setConversation(prev => [...prev, {
            type: 'error',
            message: `❌ Upload failed: ${errorData.message}\n\n📁 **File Limit:**\n• Maximum files allowed: 100\n• Please reduce the number of files and try again.`
          }]);
        } else {
          throw new Error(errorData.error || 'Upload failed');
        }
      }
    } catch (error) {
      if (!error.message.includes('Upload blocked')) {
        setConversation(prev => [...prev, {
          type: 'error',
          message: 'Failed to upload files. Please try again.'
        }]);
      }
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setTotalChunks(0);
        setCompletedChunks(0);
        setAwsProcessing(false);
        setAwsProgress(0);
        setAwsCurrentFile(0);
        setAwsTotalFiles(0);
        setAwsEstimatedTime(0);
      }, 1000);
    }
  };

  // Core search logic without adding user message to conversation
  const performSearch = async (searchQuery, documentIds = null) => {
    console.log('🚀 performSearch called with:', { searchQuery, documentIds });
    setSearching(true);
    setSearchProgress(0);
    setHasStartedNewConversation(true); // Mark that user has started a new conversation

    // Start progress bar timer (60 seconds = 100%, 1 second = ~1.67%)
    const progressInterval = setInterval(() => {
      setSearchProgress(prev => {
        if (prev >= 96) return 96; // Cap at 96% until response is received
        return prev + 1.67; // 100% over 60 seconds
      });
    }, 1000);

    try {
      console.log('📡 Making API request to /api/ai-search/query');
      const response = await fetch('/api/ai-search/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          question: searchQuery,
          ...(documentIds && { documentIds })
        }),
      });

      console.log('📡 API response received:', { status: response.status, ok: response.ok });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ API response successful:', result);
        setSearchProgress(100); // Complete the progress bar
        setConversation(prev => [...prev, {
          type: 'assistant',
          message: result.answer,
          sources: result.sources,
          conversationId: result.conversationId,
          precision: result.precision, // Include precision info
          gammaUrl: null, // Initialize Gamma slide URL
          exportUrl: null // Initialize PPTX export URL
        }]);
        
        // Generate follow-up questions based on AI response
        console.log('🤔 Generating follow-up questions...');
        await generateFollowUpQuestions(searchQuery, result.answer, result.sources, result.conversationId);

      } else {
        console.error('❌ API response not ok:', response.status, response.statusText);
        throw new Error('Search failed');
      }
    } catch (error) {
      console.error('❌ Error in performSearch:', error);
      setConversation(prev => [...prev, {
        type: 'error',
        message: 'Failed to get response. Please try again.'
      }]);
    } finally {
      console.log('🧹 Cleaning up performSearch');
      clearInterval(progressInterval);
      setSearching(false);
      setSearchProgress(0);
    }
  };

  const handleSearch = async (customQuery = null) => {
    const searchQuery = customQuery || query;
    if (!searchQuery || typeof searchQuery !== 'string' || !searchQuery.trim()) return;

    // Add user message to conversation
    const userMessage = { type: 'user', message: searchQuery };
    setConversation(prev => [...prev, userMessage]);

    // Perform the search
    await performSearch(searchQuery);
    
    // Clear query if not custom
    if (!customQuery) {
      setQuery('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const clearChat = async () => {
    if (!window.confirm('Are you sure you want to archive the current conversation? This will move it to the archive and clear the current chat.')) {
      return;
    }
    
    setConversation([]);
    setDefaultQuestions([]);
    setFollowUpQuestions([]);
    setHasStartedNewConversation(false); // Reset the flag when clearing chat
    // Archive conversation history instead of deleting
    try {
      const response = await fetch('/api/ai-search/conversations', {
        method: 'DELETE'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Archived ${data.archivedCount} conversations successfully!`);
        // Refresh archived conversations list
        await fetchArchivedConversations();
      }
    } catch (error) {
      console.error('Error archiving conversation history:', error);
      toast.error('Failed to archive conversations');
    }
  };

  // Handle user voting on messages
  const handleVote = async (messageId, vote) => {
    setUserVotes(prev => ({ ...prev, [messageId]: vote }));
    
    try {
      const response = await fetch('/api/feedback/vote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messageId,
          vote,
          timestamp: new Date().toISOString()
        }),
      });
      
      if (response.ok) {
        if (vote === 'up') {
          toast.success('Thank you for your feedback!');
        }
      } else {
        throw new Error('Failed to submit vote');
      }
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Failed to submit vote');
    }
  };

  // Handle feedback submission for thumbs down
  const handleFeedback = async (feedbackData) => {
    try {
      const response = await fetch('/api/feedback/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });
      
      if (response.ok) {
        toast.success('Thank you for your detailed feedback!');
        closeFeedbackModal();
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  // Open feedback modal
  const openFeedbackModal = (messageId, voteType) => {
    setFeedbackModal({ isOpen: true, messageId, voteType });
  };

  // Close feedback modal
  const closeFeedbackModal = () => {
    setFeedbackModal({ isOpen: false, messageId: null });
  };

  // Format markdown for rich text display
  const formatMarkdown = (text) => {
    if (!text) return '';
    
    return text
      // Headers with emojis
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-gray-900 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-gray-900 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-gray-900 mb-4">$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      
      // Lists
      .replace(/^\* (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
      
      // Code blocks
      .replace(/```(.*?)```/gs, '<pre class="bg-gray-100 p-2 rounded text-sm font-mono overflow-x-auto my-2">$1</pre>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded text-sm font-mono">$1</code>')
      
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-sylvan-600 hover:text-sylvan-800 underline" target="_blank" rel="noopener noreferrer">$1</a>')
      
      // Tables - convert markdown tables to HTML
      .replace(/\|([^\n]+)\|\n\|[-\s|:]+\|\n((?:\|[^\n]+\|\n?)*)/g, (match, headerRow, bodyRows) => {
        const headers = headerRow.split('|').map(h => h.trim()).filter(h => h);
        const rows = bodyRows.trim().split('\n').map(row => 
          row.split('|').map(cell => cell.trim()).filter(cell => cell)
        );
        
        let tableHtml = '<div class="overflow-x-auto my-4"><table class="min-w-full divide-y divide-gray-200 border border-gray-300 rounded-lg">';
        
        // Header
        tableHtml += '<thead class="bg-gray-50">';
        tableHtml += '<tr>';
        headers.forEach(header => {
          tableHtml += `<th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">${header}</th>`;
        });
        tableHtml += '</tr></thead>';
        
        // Body
        tableHtml += '<tbody class="bg-white divide-y divide-gray-200">';
        rows.forEach(row => {
          tableHtml += '<tr class="hover:bg-gray-50">';
          row.forEach(cell => {
            tableHtml += `<td class="px-3 py-2 text-sm text-gray-900 border-b border-gray-100">${cell}</td>`;
          });
          tableHtml += '</tr>';
        });
        tableHtml += '</tbody></table></div>';
        
        return tableHtml;
      })
      
      // Line breaks
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br>')
      
      // Wrap in paragraph tags
      .replace(/^(.*)$/gm, '<p class="mb-2">$1</p>')
      .replace(/<p class="mb-2"><\/p>/g, '')
      .replace(/<p class="mb-2"><br><\/p>/g, '');
  };

  // Auto-generate summary for uploaded documents
  const generateAutoSummary = async (uploadResults) => {
    try {
      if (!uploadResults || uploadResults.length === 0) return;
      
      const successfulUploads = uploadResults.filter(result => result.status === 'success');
      if (successfulUploads.length === 0) return;
      
      // Create auto-summary query
      const filenames = successfulUploads.map(result => result.filename).join(', ');
      const documentIds = successfulUploads.map(result => result.documentId);
      const autoQuery = `Please provide a comprehensive summary of the uploaded document(s): ${filenames}. Include key information, main topics, data points, and structure overview.`;
      
      // Add user query to conversation ONLY ONCE here
      setConversation(prev => [...prev, {
        type: 'user',
        message: autoQuery
      }]);
      
      // Call the search function with specific document IDs
      await performSearch(autoQuery, documentIds);
      
    } catch (error) {
      console.error('❌ Error generating auto summary:', error);
      setConversation(prev => [...prev, {
        type: 'system',
        message: 'Auto-summary generation failed, but documents are ready for manual queries.'
      }]);
    }
  };

  // Generate default questions based on uploaded documents
  const generateDefaultQuestions = async (uploadResults) => {
    try {
      const successResults = uploadResults.filter(r => r.status === 'success');
      if (successResults.length === 0) return;

      const documentIds = successResults.map(r => r.documentId).filter(id => id);
      const filenames = successResults.map(r => r.filename).filter(name => name);

      if (documentIds.length === 0) return;

      const response = await fetch('/api/ai-search/generate-default-questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ documentIds, filenames }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.questions) {
          setDefaultQuestions(result.questions);
        }
      }
    } catch (error) {
      console.error('Error generating default questions:', error);
    }
  };

  // Generate follow-up questions based on AI response
  const generateFollowUpQuestions = async (userQuestion, aiResponse, sources, conversationId) => {
    try {
      const response = await fetch('/api/ai-search/generate-followups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userQuestion,
          aiResponse,
          sources,
          conversationId,
          conversationHistory: conversation.slice(-6) // Last 6 messages for context
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.followUps) {
          setFollowUpQuestions(result.followUps);
        }
      }
    } catch (error) {
      console.error('Error generating follow-up questions:', error);
    }
  };

  // Auto-scroll to bottom when conversation updates
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversation]);

  // Auto-scroll to bottom when page loads with existing conversation
  useEffect(() => {
    if (chatContainerRef.current && conversation.length > 0) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }, 100);
    }
  }, [conversation.length]);

  // Export functions for AI search results
  const copyToClipboard = async (message) => {
    try {
      // Keep original markdown formatting for clipboard
      const content = `Question: ${message.question || 'N/A'}\n\nAnswer:\n${message.message}`;
      
      if (message.sources && message.sources.length > 0) {
        const sourcesText = message.sources.map(source => 
          `- ${source.filename} (${source.similarityPercentage}% match)`
        ).join('\n');
        const fullContent = `${content}\n\nSources:\n${sourcesText}`;
        await navigator.clipboard.writeText(fullContent);
      } else {
        await navigator.clipboard.writeText(content);
      }
      
      toast.success('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const downloadAsTxt = (message, index) => {
    try {
      // Convert markdown to readable plain text while preserving structure
      let content = message.message
        // Convert headers
        .replace(/^### (.*$)/gim, '\n$1\n' + '='.repeat(20))
        .replace(/^## (.*$)/gim, '\n$1\n' + '='.repeat(30))
        .replace(/^# (.*$)/gim, '\n$1\n' + '='.repeat(40))
        // Convert bold and italic
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        // Convert lists
        .replace(/^[*-] (.*$)/gim, '• $1')
        // Convert code blocks
        .replace(/```(.*?)```/gs, '\n--- CODE ---\n$1\n--- END CODE ---\n')
        .replace(/`(.*?)`/g, '"$1"')
        // Clean up multiple newlines
        .replace(/\n{3,}/g, '\n\n');

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `ai-search-result-${index}-${timestamp}.txt`;
      
      let fullContent = `AI Review Result\n${'='.repeat(50)}\n\n`;
      if (message.question) {
        fullContent += `Question: ${message.question}\n\n`;
      }
      fullContent += `Answer:\n${content}\n\n`;
      
      if (message.sources && message.sources.length > 0) {
        fullContent += `Sources:\n${'='.repeat(20)}\n`;
        message.sources.forEach(source => {
          fullContent += `• ${source.filename} (${source.similarityPercentage}% match)\n`;
        });
      }
      
      if (message.precision) {
        fullContent += `\nPrecision: ${message.precision.level} (${message.precision.averageSimilarityPercentage}%)\n`;
      }
      
      const blob = new Blob([fullContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Downloaded as TXT file!');
    } catch (error) {
      console.error('Failed to download TXT:', error);
      toast.error('Failed to download TXT file');
    }
  };



    // Gamma API integration for generating beautiful slides
  const generateGammaSlides = async (message, index) => {
    let progressInterval; // Declare at function scope
    
    try {
      console.log('🎯 Starting Gamma slides generation...');
      console.log(`📊 Message index: ${index}`);
      
      // Validate message format first
      if (!message || !message.message || typeof message.message !== 'string') {
        console.error('❌ Invalid message format:', message);
        toast.error('Invalid message format for slide generation');
        return;
      }
      
      console.log(`📝 Original message length: ${message.message.length} characters`);
      
      setGammaLoading(prev => ({ ...prev, [index]: true }));

      // Calculate dynamic number of cards based on word count (1 slide per 60 words)
      const textLength = message.message.length;
      const wordCount = message.message.trim().split(/\s+/).length;
      let numCards = Math.min(60, Math.max(3, Math.ceil(wordCount / 60))); // 1 slide per 60 words, minimum 3, maximum 60

      console.log(`📊 Calculated numCards: ${numCards} (based on ${wordCount} words, ${textLength} characters, capped at 60)`);

      // Prepare text content for Gamma API
      const inputText = message.message
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '') // Remove emojis
        .replace(/\n+/g, ' ') // Convert newlines to spaces
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

      console.log(`🧹 Cleaned text length: ${inputText.length} characters`);
      console.log(`📝 Text preview: ${inputText.substring(0, 100)}...`);

      console.log('🚀 Calling backend endpoint to create presentation...');
      
      // Call our backend endpoint to create presentation
      const response = await fetch('/api/ai-search/gamma-slides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inputText: inputText,
          numCards: numCards
        })
      });

      console.log(`📡 Backend response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Backend error response: ${errorText}`);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log(`📄 Backend response data:`, data);
      
      const generationId = data.generationId;

      if (!generationId) {
        console.error('❌ No generation ID in backend response');
        throw new Error('No generation ID received from server');
      }

      console.log(`✅ Generation started successfully`);
      console.log(`🆔 Generation ID: ${generationId}`);
      console.log(`📊 Request payload: numCards=${numCards}, inputTextLength=${inputText.length}`);

      toast.success(`🎯 Generating ${numCards} slides... This may take a few minutes.`);

            // Start progress timer
      const startTime = Date.now();
      const maxWaitTime = 5 * 60 * 1000; // 5 minutes for large slide decks
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, maxWaitTime - elapsed);
        const progressPercent = Math.min(95, (elapsed / maxWaitTime) * 95); // Cap at 95% until actual completion
        
        setGammaProgress(prev => ({
          ...prev,
          [index]: {
            elapsed: Math.floor(elapsed / 1000),
            remaining: Math.floor(remaining / 1000),
            progress: progressPercent
          }
        }));
      };
      
      // Update progress every second
      progressInterval = setInterval(updateProgress, 1000);
      updateProgress(); // Initial update
      
      // Poll for completion and get the final URL
      let attempts = 0;
      
      const pollForCompletion = async () => {
        try {
          attempts++;
          console.log(`🔍 Polling attempt ${attempts} for generation ${generationId}`);
          
          const statusResponse = await fetch(`/api/ai-search/gamma-slides/${generationId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          console.log(`📡 Status check response: ${statusResponse.status} ${statusResponse.statusText}`);

          if (!statusResponse.ok) {
            const errorText = await statusResponse.text();
            console.error(`❌ Status check failed: ${statusResponse.status} - ${errorText}`);
            throw new Error(`Status check failed: ${statusResponse.status} - ${errorText}`);
          }

          const statusData = await statusResponse.json();
          console.log(`📄 Status check data:`, statusData);

          if (statusData.status === 'completed') {
            console.log(`✅ Generation completed successfully`);
            console.log(`🔗 Gamma URL: ${statusData.gammaUrl || 'Not available'}`);
            console.log(`📊 Export URL: ${statusData.exportUrl || 'Not available'}`);
            
            // Clear progress timer and loading state
            clearInterval(progressInterval);
            setGammaLoading(prev => ({ ...prev, [index]: false }));
            
            // Show completion message and buttons
            console.log('🎉 Slides generation completed successfully!');
            console.log(`🔗 Gamma URL: ${statusData.gammaUrl || 'Not available'}`);
            console.log(`📊 Export URL: ${statusData.exportUrl || 'Not available'}`);
            
            // Set the completed state with URLs immediately, preserving progress data
            setGammaProgress(prev => ({
              ...prev,
              [index]: {
                ...prev[index], // Preserve existing progress data
                completed: true,
                gammaUrl: statusData.gammaUrl,
                exportUrl: statusData.exportUrl
              }
            }));
            
            // Update the conversation state to persist the Gamma slide links
            setConversation(prev => prev.map((msg, i) => 
              i === index ? {
                ...msg,
                gammaUrl: statusData.gammaUrl,
                exportUrl: statusData.exportUrl
              } : msg
            ));
            
            // Also update the conversation in the database for persistence
            if (message.conversationId && (statusData.gammaUrl || statusData.exportUrl)) {
              try {
                await fetch(`/api/ai-search/conversations/${message.conversationId}/gamma-links`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    gammaUrl: statusData.gammaUrl,
                    exportUrl: statusData.exportUrl
                  }),
                });
                console.log('✅ Conversation updated in database with Gamma slide links');
              } catch (error) {
                console.error('❌ Failed to update conversation in database:', error);
              }
            }
            
            // Show success toast
            toast.success('🎉 Slides are ready! Check the buttons below.');
            return;
          }

          console.log(`⏳ Generation still in progress. Status: ${statusData.status}. Attempt ${attempts}`);
          
          // Keep polling indefinitely - no timeout
          // Wait 10 seconds before next poll
          console.log(`⏰ Waiting 10 seconds before next poll...`);
          setTimeout(pollForCompletion, 10000);

        } catch (error) {
          console.error('❌ Error during status polling:', error);
          console.log(`🔄 Retrying after error. Attempt ${attempts}`);
          
          // Keep retrying even on error
          console.log(`⏰ Waiting 10 seconds before retry...`);
          setTimeout(pollForCompletion, 10000);
        }
      };

      // Start polling
      console.log('🚀 Starting status polling...');
      pollForCompletion();

    } catch (error) {
      console.error('❌ Failed to generate Gamma slides:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        index: index
      });
      clearInterval(progressInterval);
      setGammaLoading(prev => ({ ...prev, [index]: false }));
      setGammaProgress(prev => ({ ...prev, [index]: undefined }));
      toast.error('Failed to generate slides: ' + error.message);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Review</h1>
          <p className="text-gray-600">
            Upload documents for comprehensive AI-powered analysis, review, and strategic insights.
          </p>
        </div>
        
        {/* Compact Upload Section - Right Top */}
        <div className="flex items-center space-x-3">
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.txt,.doc,.docx,.csv,.json,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
            >
              <Upload className="mr-2" size={16} />
              Upload Files
            </button>
          </div>
          <button
            onClick={() => setShowDocumentManager(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
          >
            <FileText className="mr-2" size={16} />
            Manage
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-4">
        {/* File Status Section - Only show when files are selected */}
        {files.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Selected Files ({files.length}/100)</h3>
              <button
                onClick={() => setFiles([])}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            </div>
            
            <div className="space-y-2">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="mr-2" size={14} />
                    <span className="text-sm text-gray-700">{file.name}</span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end mt-3">
              <button
                onClick={uploadAndProcess}
                disabled={uploading}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={14} />
                    Processing... {uploadProgress > 0 ? `${uploadProgress}%` : ''}
                    {totalChunks > 0 && ` (${completedChunks}/${totalChunks} chunks)`}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2" size={14} />
                    Upload & Process
                  </>
                )}
              </button>
            </div>
            
            {uploading && uploadProgress > 0 && (
              <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-green-600 h-1.5 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
            
            {/* PDF OCR Processing Progress */}
            {awsProcessing && (
              <div className="mt-2 p-2 bg-sylvan-50 border border-sylvan-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-sylvan-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-sylvan-700">
                    PDF OCR Processing
                  </span>
                  <span className="text-xs text-sylvan-600">
                    File {awsCurrentFile}/{awsTotalFiles}
                  </span>
                </div>
                
                <div className="w-full bg-sylvan-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-sylvan-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${awsProgress}%` }}
                  ></div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-sylvan-600">
                  <span>{awsProgress}% Complete</span>
                  <span>
                    {awsProgress < 100 ? `⏱️ ~${awsEstimatedTime} min remaining` : '✅ Processing complete'}
                  </span>
                </div>
                
                <div className="mt-2 text-xs text-blue-500">
                  💡 Processing rotated/OCR PDFs for better text extraction
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chat Interface */}
        <div className="w-full flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[700px] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Search className="mr-2" size={20} />
                {showArchived ? 'Archived Conversations' : 'Chat with Documents'}
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setShowArchived(!showArchived);
                    if (!showArchived) {
                      fetchArchivedConversations();
                    }
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                >
                  {showArchived ? 'Show Active Chat' : 'View Archive'}
                </button>
                {conversation.length > 0 && !showArchived && (
                  <button
                    onClick={clearChat}
                    className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                  >
                    Clear Chat
                  </button>
                )}
              </div>
            </div>
            
            {/* Chat Messages or Archived Conversations */}
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 pb-8">
              {showArchived ? (
                // Archived Conversations View
                <div className="space-y-4">
                  {archivedConversations.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p>No archived conversations found.</p>
                    </div>
                  ) : (
                    archivedConversations.map((conv, index) => (
                      <div key={conv.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start mb-3">
                          <div className="text-xs text-gray-500">
                            Archived: {new Date(conv.archived_at).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            Created: {new Date(conv.created_at).toLocaleString()}
                          </div>
                        </div>
                        
                        {/* User Question */}
                        <div className="flex justify-end mb-2">
                          <div className="max-w-xs lg:max-w-md px-3 py-2 rounded-lg bg-sylvan-600 text-white">
                            <p className="text-sm whitespace-pre-wrap">{conv.question}</p>
                          </div>
                        </div>
                        
                        {/* AI Answer */}
                        <div className="flex justify-start">
                          <div className="max-w-full px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-800">
                            <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: formatMarkdown(conv.answer) }} />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                // Active Chat View
                conversation.length === 0 ? null : (
                  <div className="space-y-1">
                    {conversation.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${
                        message.type === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`${
                          message.type === 'user' 
                            ? 'max-w-xs lg:max-w-2xl xl:max-w-4xl' 
                            : 'max-w-full'
                        } px-3 py-2 rounded-lg ${
                          message.type === 'user'
                            ? 'bg-sylvan-600 text-white'
                            : message.type === 'assistant'
                            ? 'bg-white border border-gray-200 text-gray-800'
                            : message.type === 'system'
                            ? 'bg-green-100 border border-green-200 text-green-800'
                            : 'bg-red-100 border border-red-200 text-red-800'
                        }`}
                      >
                        <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: formatMarkdown(message.message || '') }} />
                        {message.sources && message.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs font-medium">Sources:</p>
                              {message.precision && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  message.precision.level === 'Ultra-High' ? 'bg-green-100 text-green-800' :
                                  message.precision.level === 'High' ? 'bg-blue-100 text-blue-800' :
                                  message.precision.level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {message.precision.level} Precision ({message.precision.averageSimilarityPercentage}%)
                                </span>
                              )}
                            </div>
                            <div className="space-y-1">
                              {message.sources.map((source, idx) => (
                                <div key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded flex justify-between">
                                  <span>{source.filename}</span>
                                  <span className="font-medium">{source.similarityPercentage}% match</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Voting Component for Assistant Messages */}
                        {message.type === 'assistant' && (
                          <div className="mt-3 pt-2 border-t border-gray-200">
                            <MessageVoting
                              messageId={`ai-search-${index}`}
                              userVote={userVotes[`ai-search-${index}`]}
                              onVote={handleVote}
                              onFeedback={openFeedbackModal}
                            />
                          </div>
                        )}

                        {/* Export Options for Assistant Messages */}
                        {message.type === 'assistant' && (
                          <div className="mt-3 pt-2 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-500">Export Options:</span>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => copyToClipboard({
                                    ...message,
                                    question: conversation[index - 1]?.message // Get the previous user question
                                  })}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
                                  title="Copy to clipboard (preserves markdown formatting)"
                                >
                                  <Copy size={12} className="mr-1" />
                                  Copy
                                </button>
                                <button
                                  onClick={() => downloadAsTxt({
                                    ...message,
                                    question: conversation[index - 1]?.message
                                  }, index)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
                                  title="Download as plain text file (structured format)"
                                >
                                  <FileType size={12} className="mr-1" />
                                  TXT
                                </button>
                                
                 {/* Show Slides Button or Action Buttons */}
                 {!gammaProgress[index] || !gammaProgress[index].completed ? (
                   <button
                     onClick={() => generateGammaSlides({
                       ...message,
                       question: conversation[index - 1]?.message
                     }, index)}
                     disabled={gammaLoading[index]}
                     className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                     title="Generate beautiful slides with Gamma AI (may take a few minutes)"
                   >
                     {gammaLoading[index] ? (
                       <Loader2 size={12} className="mr-1 animate-spin" />
                     ) : (
                       <Presentation size={12} className="mr-1" />
                     )}
                     {gammaLoading[index] ? 'Generating...' : 'Slides'}
                   </button>
                 ) : (
                   // Show Download and Web buttons in place of Slides button
                   <div className="flex space-x-1">
                     {(gammaProgress[index]?.gammaUrl || message.gammaUrl) && (
                       <button
                         onClick={() => window.open(gammaProgress[index]?.gammaUrl || message.gammaUrl, '_blank')}
                         className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-sylvan-600 hover:bg-sylvan-700 rounded transition-colors"
                         title="Open Gamma presentation in new tab"
                       >
                         🌐 Web
                       </button>
                     )}
                     {(gammaProgress[index]?.exportUrl || message.exportUrl) && (
                       <a
                         href={gammaProgress[index]?.exportUrl || message.exportUrl}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                         title={`Open ${getExportIcon(gammaProgress[index]?.exportUrl || message.exportUrl) === '📄' ? 'PDF' : 'PPTX'} in new tab`}
                       >
                         {getExportIcon(gammaProgress[index]?.exportUrl || message.exportUrl)} {getExportIcon(gammaProgress[index]?.exportUrl || message.exportUrl) === '📄' ? 'PDF' : 'PPTX'}
                       </a>
                       )}
                   </div>
                 )}
                 
                 {/* Progress Timer Display */}
                 {gammaProgress[index] && !gammaProgress[index].completed && (
                   <div className="mt-2 text-xs text-gray-600">
                     <div className="flex items-center justify-between mb-1">
                       <span>⏱️ {gammaProgress[index].elapsed}s elapsed</span>
                       <span>⏳ {gammaProgress[index].remaining}s remaining</span>
                     </div>
                     <div className="w-full bg-gray-200 rounded-full h-1.5">
                       <div 
                         className="bg-sylvan-600 h-1.5 rounded-full transition-all duration-300 ease-out" 
                         style={{ width: `${gammaProgress[index].progress}%` }}
                       ></div>
                     </div>
                   </div>
                 )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {searching && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-gray-200 text-gray-800 px-4 py-3 rounded-lg min-w-[300px]">
                        <div className="flex items-center space-x-3 mb-2">
                          <Loader2 className="animate-spin" size={16} />
                          <span className="text-sm font-medium">Analyzing documents...</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className="bg-sylvan-600 h-2 rounded-full transition-all duration-300 ease-out" 
                            style={{ width: `${searchProgress}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Processing...</span>
                          <span>{searchProgress}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
            </div>
            
            {/* Chat Input - Only show when not viewing archived conversations */}
            {!showArchived && (
              <div className="border-t border-gray-200 p-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask a question about your uploaded documents..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sylvan-500 focus:border-transparent"
                    disabled={searching}
                  />
                  <button
                    onClick={() => {
                      console.log('🖱️ Search button clicked!');
                      handleSearch();
                    }}
                    disabled={searching || !query || typeof query !== 'string' || !query.trim()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50"
                  >
                    {searching ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
                
                {/* Default Questions - Only show when not viewing archived conversations */}
                {defaultQuestions.length > 0 && !hasStartedNewConversation && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2 mb-3">
                      <Search className="h-4 w-4 text-sylvan-600" />
                      <span className="text-sm font-medium text-gray-700">Suggested questions to get started:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {defaultQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => handleSearch(question)}
                          className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-medium transition-colors border border-blue-200"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Follow-up Questions - Only show when not viewing archived conversations */}
                {followUpQuestions.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2 mb-3">
                      <Search className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Follow-up questions:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {followUpQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => handleSearch(question)}
                          className="px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-full text-xs font-medium transition-colors border border-green-200"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedbackModal}
        onSubmit={handleFeedback}
        messageId={feedbackModal.messageId}
        voteType={feedbackModal.voteType}
        userQuestion={conversation.find((_, idx) => `ai-search-${idx}` === feedbackModal.messageId)?.message || ''}
        context="ai-search"
      />

      {/* Document Manager Modal */}
      {showDocumentManager && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="mr-2" size={20} />
                Document Management
              </h3>
              <button
                onClick={() => setShowDocumentManager(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <DocumentManager onDocumentUpdate={() => setConversation([])} ref={documentManagerRef} />
          </div>
        </div>
      )}

    </div>
  );
};

export default AISearch; 